import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, map, Observable, of, shareReplay } from 'rxjs';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
  type WritableStateSource,
} from '@ngrx/signals';
import { tapResponse } from '@ngrx/operators';

import { environment } from '../../../environments/environment';
import { AuthSessionResponse, User, UserOrganization, UserRole } from '../models/user.model';
import { AuthService } from '../services/auth.service';
import { AUTH_COPY } from '../branding/auth-copy.config';

export const AUTH_TOKEN_KEY = 'auth_token';
export const AUTH_USER_KEY = 'auth_user';
export const AUTH_ROLE_KEY = 'auth_role';
export const AUTH_SELECTED_ORG_KEY = 'auth_selected_org';

const ASSESSMENT_PORTAL_ROLES: readonly UserRole[] = ['SITEMANAGER', 'EDUCATOR'];

export interface LoadSessionOptions {
  forceRefresh?: boolean;
}

interface AuthState {
  currentUser: User | null;
  currentUserRole: UserRole | null;
  organizations: UserOrganization[];
  /**
   * Site the user is currently viewing, for multi-site site managers using the
   * site switcher. null when the switcher does not apply (single-site users,
   * educators, admins) — in which case no per-site request scoping happens.
   */
  selectedOrganizationId: number | null;
  isAuthenticated: boolean;
  sessionChecked: boolean;
  loading: boolean;
  error: string | null;
}

function emptyAuthState(): AuthState {
  return {
    currentUser: null,
    currentUserRole: null,
    organizations: [],
    selectedOrganizationId: null,
    isAuthenticated: false,
    sessionChecked: false,
    loading: false,
    error: null,
  };
}

function getInitialAuthState(): AuthState {
  sessionStorage.removeItem(AUTH_TOKEN_KEY);
  if (environment.useMockApi) {
    const storedUser = readStoredUser();
    const storedRole = readStoredRole();
    if (storedUser && storedRole) {
      return {
        currentUser: storedUser,
        currentUserRole: storedRole,
        organizations: [],
        selectedOrganizationId: null,
        isAuthenticated: true,
        sessionChecked: true,
        loading: false,
        error: null,
      };
    }
  }
  clearStoredSessionIdentity();
  return emptyAuthState();
}

function readStoredUser(): User | null {
  const raw = sessionStorage.getItem(AUTH_USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

function readStoredRole(): UserRole | null {
  const raw = sessionStorage.getItem(AUTH_ROLE_KEY);
  if (!raw) return null;
  return raw as UserRole;
}

function clearStoredAuth(): void {
  clearStoredSessionIdentity();
  sessionStorage.removeItem(AUTH_SELECTED_ORG_KEY);
}

function clearStoredSessionIdentity(): void {
  sessionStorage.removeItem(AUTH_TOKEN_KEY);
  sessionStorage.removeItem(AUTH_USER_KEY);
  sessionStorage.removeItem(AUTH_ROLE_KEY);
}

function readSelectedOrg(): number | null {
  const raw = sessionStorage.getItem(AUTH_SELECTED_ORG_KEY);
  if (raw === null) return null;
  const parsed = Number(raw);
  return Number.isInteger(parsed) ? parsed : null;
}

function persistSelectedOrg(id: number | null): void {
  if (id === null) sessionStorage.removeItem(AUTH_SELECTED_ORG_KEY);
  else sessionStorage.setItem(AUTH_SELECTED_ORG_KEY, String(id));
}

function persistSession(user: User, role: UserRole | null): void {
  sessionStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
  if (role) sessionStorage.setItem(AUTH_ROLE_KEY, role);
  else sessionStorage.removeItem(AUTH_ROLE_KEY);
}

function hasAnyRole(
  primaryRole: UserRole | null,
  organizations: UserOrganization[],
  roles: UserRole[]
): boolean {
  const assignedRoles = new Set<UserRole>();
  if (primaryRole) assignedRoles.add(primaryRole);
  organizations.forEach((organization) => assignedRoles.add(organization.Role));

  return roles.some((role) => assignedRoles.has(role));
}

function selectActiveOrganization(organizations: UserOrganization[]): UserOrganization | null {
  return (
    organizations.find((organization) => ASSESSMENT_PORTAL_ROLES.includes(organization.Role)) ??
    organizations[0] ??
    null
  );
}

/**
 * Settings where the user is a site manager. Phase 1 of the site switcher is
 * scoped to site managers only — multi-site educators keep the combined view.
 */
function siteManagerSitesOf(organizations: UserOrganization[]): UserOrganization[] {
  return organizations.filter((organization) => organization.Role === 'SITEMANAGER');
}

/**
 * Default selected site after a session loads: keep the persisted choice when
 * it is still a valid site-manager site, otherwise fall back to the first one.
 * Returns null (no switcher, no per-site scoping) unless the user manages more
 * than one site.
 */
function resolveInitialSelectedOrg(organizations: UserOrganization[]): number | null {
  const siteManagerSites = siteManagerSitesOf(organizations);
  if (siteManagerSites.length <= 1) {
    persistSelectedOrg(null);
    return null;
  }
  const persisted = readSelectedOrg();
  const selected =
    persisted !== null && siteManagerSites.some((site) => site.OrganizationID === persisted)
      ? persisted
      : siteManagerSites[0].OrganizationID;
  persistSelectedOrg(selected);
  return selected;
}

function applySession(store: WritableStateSource<AuthState>, session: AuthSessionResponse): void {
  const role = session.role ?? null;
  persistSession(session.user, role);
  const organizations = session.organizations ?? [];
  patchState(store, {
    currentUser: session.user,
    currentUserRole: role,
    organizations,
    selectedOrganizationId: resolveInitialSelectedOrg(organizations),
    isAuthenticated: true,
    sessionChecked: true,
    loading: false,
    error: null,
  });
}

export const AuthStore = signalStore(
  { providedIn: 'root' },

  withState<AuthState>(() => getInitialAuthState()),

  withComputed(({ currentUserRole, organizations, selectedOrganizationId }) => ({
    isSiteManager: computed(() => hasAnyRole(currentUserRole(), organizations(), ['SITEMANAGER'])),
    isSuperuser: computed(() => hasAnyRole(currentUserRole(), organizations(), ['SUPERUSER'])),
    /** Site-manager settings available to switch between. */
    siteManagerSites: computed(() => siteManagerSitesOf(organizations())),
    /** Only show the switcher when the user manages more than one site. */
    showSiteSwitcher: computed(() => siteManagerSitesOf(organizations()).length > 1),
    /**
     * Org id the data views should scope requests to. Non-null only when the
     * switcher is active, so single-site users / educators / admins are
     * completely unaffected (no organizationId param is sent).
     */
    selectedOrganizationIdForRequests: computed(() =>
      siteManagerSitesOf(organizations()).length > 1 ? selectedOrganizationId() : null
    ),
    activeOrganization: computed(() => {
      const orgs = organizations();
      const selectedId = selectedOrganizationId();
      if (selectedId !== null) {
        const match = orgs.find((organization) => organization.OrganizationID === selectedId);
        if (match) return match;
      }
      return selectActiveOrganization(orgs);
    }),
  })),

  withMethods((store, service = inject(AuthService), router = inject(Router)) => {
    let inFlightSession$: Observable<boolean> | null = null;

    return {
      loadSession(options: LoadSessionOptions = {}): Observable<boolean> {
        if (inFlightSession$) {
          return inFlightSession$;
        }

        if (store.sessionChecked() && !options.forceRefresh) {
          return of(store.isAuthenticated());
        }

        patchState(store, { loading: true, error: null });

        inFlightSession$ = service.getCurrentSession().pipe(
          map((session) => {
            applySession(store, session);
            return true;
          }),
          catchError(() => {
            clearStoredAuth();
            patchState(store, { ...emptyAuthState(), sessionChecked: true });
            return of(false);
          }),
          finalize(() => {
            inFlightSession$ = null;
          }),
          shareReplay({ bufferSize: 1, refCount: false })
        );

        return inFlightSession$;
      },

      login(credentials: { email: string; password: string }): void {
        patchState(store, { loading: true, error: null });

        if (!environment.useMockApi) {
          service.startLogin();
          return;
        }

        service
          .mockLogin(credentials.email, credentials.password)
          .pipe(
            tapResponse({
              next: (session) => {
                applySession(store, session);
                router.navigate([session.role === 'SUPERUSER' ? '/admin' : '/dashboard']);
              },
              error: () =>
                patchState(store, {
                  error: 'Invalid credentials. Please try again.',
                  loading: false,
                }),
            })
          )
          .subscribe();
      },

      changePassword(request: { currentPassword: string; newPassword: string }): void {
        patchState(store, { loading: true, error: null });

        if (!environment.useMockApi) {
          patchState(store, {
            error: AUTH_COPY.passwordManagedMessage,
            loading: false,
          });
          return;
        }

        service
          .changePassword(request.currentPassword, request.newPassword)
          .pipe(
            tapResponse({
              next: () => {
                patchState(store, { loading: false });
                router.navigate(['/dashboard']);
              },
              error: () =>
                patchState(store, {
                  error: 'Failed to change password. Please check your current password.',
                  loading: false,
                }),
            })
          )
          .subscribe();
      },

      logout(): void {
        service.logout().subscribe({
          next: () => {
            clearStoredAuth();
            patchState(store, { ...emptyAuthState(), sessionChecked: true });
            router.navigate(['/login']);
          },
          error: () => {
            clearStoredAuth();
            patchState(store, { ...emptyAuthState(), sessionChecked: true });
            router.navigate(['/login']);
          },
        });
      },

      clearSession(): void {
        clearStoredAuth();
        patchState(store, { ...emptyAuthState(), sessionChecked: true });
      },

      updateCurrentUser(updates: Partial<User>): void {
        const current = store.currentUser();
        if (current) {
          const updated = { ...current, ...updates };
          patchState(store, { currentUser: updated });
          sessionStorage.setItem(AUTH_USER_KEY, JSON.stringify(updated));
        }
      },

      hasRole(roles: UserRole[]): boolean {
        return hasAnyRole(store.currentUserRole(), store.organizations(), roles);
      },

      /**
       * Switch the active site for a multi-site site manager. Ignored if the id
       * is not one of the caller's SITEMANAGER settings (defends against a
       * stale/forged value). Persisted so the choice survives a reload.
       */
      setSelectedOrganization(organizationId: number): void {
        const isSiteManagerSite = store
          .organizations()
          .some(
            (organization) =>
              organization.OrganizationID === organizationId && organization.Role === 'SITEMANAGER'
          );
        if (!isSiteManagerSite) return;
        persistSelectedOrg(organizationId);
        patchState(store, { selectedOrganizationId: organizationId });
      },
    };
  })
);
